import React, { useEffect, useRef, useState } from 'react';
import styles from './voice.module.css';
import { isRtlVoice, voiceText } from './voiceStrings';

/**
 * ONE VOICE BUTTON for both apps.
 *   tap          → onTap() (starts voice mode)
 *   hold         → records (the recorder from useHoldToRecord): live waveform, timer and words in <RecordingStrip>
 *   release      → sends
 *   slide up     → locks (hands-free until Send or the trash)
 *   slide toward the start edge → cancels (RTL-aware: the right edge in Hebrew and Arabic)
 *   under 0.6 s  → a small shake, no error
 * Press scale 0.96, no animation over 300 ms, reduced motion respected (voice.module.css). The haptics are the
 * recorder's (it was passed `haptic`). A keyboard user presses Enter or Space, which is a tap.
 */
/**
 * A TAP OPENS SOMETHING UNDER THE FINGER (iOS app, 26 Oct): voice mode replaces the composer, and the click iOS sends after
 * the touch ends lands on whatever is now where the mic was — voice mode's Type button — which closed it again and opened the
 * keyboard. After a tap that opened something, the next click anywhere is swallowed for a moment.
 */
const GHOST_CLICK_MS = 500;
function swallowGhostClick() {
  if (typeof document === 'undefined') return;
  const off = () => { document.removeEventListener('click', eat, true); clearTimeout(t); };
  const eat = (e) => { e.stopPropagation(); e.preventDefault(); off(); };
  const t = setTimeout(off, GHOST_CLICK_MS);
  document.addEventListener('click', eat, true);
}

const HOLD_AFTER_MS = 220; // a press this long is a hold, not a tap
const SLIDE_PX = 64;

export function VoiceButton({ recorder, onTap, language = 'en', disabled = false, active = false, className = '', title }) {
  const rtl = isRtlVoice(language);
  const [pressed, setPressed] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const st = useRef({ x: 0, y: 0, timer: null, holding: false, id: null });
  const recording = recorder.phase === 'recording' || recorder.phase === 'locked';

  useEffect(() => { if (recorder.shake) setShakeKey(recorder.shake); }, [recorder.shake]);
  useEffect(() => () => clearTimeout(st.current.timer), []);

  const down = (e) => {
    if (disabled || (e.button != null && e.button > 0)) return;
    st.current.x = e.clientX; st.current.y = e.clientY; st.current.holding = false; st.current.id = e.pointerId; st.current.touch = e.pointerType === 'touch';
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) { /* not capturable */ }
    setPressed(true);
    if (recorder.phase === 'locked') return;
    // iOS grants the microphone only inside this very handler: the press opens the ears now, the hold at 220 ms confirms them.
    recorder.arm?.();
    clearTimeout(st.current.timer);
    // The id is cleared when it fires: a pending timer is what makes the lift a TAP, so a hold that ended any other way (slide-cancel) is never one.
    st.current.timer = setTimeout(() => { st.current.timer = null; st.current.holding = true; recorder.begin(); }, HOLD_AFTER_MS);
  };
  const move = (e) => {
    if (!st.current.holding) return;
    const dx = e.clientX - st.current.x; const dy = e.clientY - st.current.y;
    if (dy < -SLIDE_PX) recorder.lock();
    else if ((rtl ? dx > SLIDE_PX : dx < -SLIDE_PX) && recorder.phase === 'recording') { st.current.holding = false; recorder.cancel(); }
  };
  const up = () => {
    setPressed(false);
    clearTimeout(st.current.timer);
    if (st.current.holding) { st.current.holding = false; recorder.release(); return; }
    if (recorder.phase === 'locked' || recorder.phase === 'sending') return;
    if (!st.current.holding && st.current.timer != null) { st.current.timer = null; recorder.disarm?.(); if (st.current.touch) swallowGhostClick(); onTap?.(); }
  };
  const cancel = () => {
    setPressed(false); clearTimeout(st.current.timer); st.current.timer = null;
    if (!st.current.holding) recorder.disarm?.();
    if (st.current.holding) { st.current.holding = false; recorder.cancel(); }
  };
  const key = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap?.(); } };

  return (
    <button
      type="button"
      key={shakeKey}
      className={`${styles.root} ${styles.button} ${pressed ? styles.pressed : ''} ${recording ? styles.recording : ''} ${active ? styles.active : ''} ${shakeKey ? styles.shake : ''} ${className}`}
      aria-label={title || voiceText(language, 'buttonName')}
      aria-pressed={recording ? 'true' : 'false'}
      disabled={disabled}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={key}
      data-testid="voice-button"
    >
      <svg className={styles.icon} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <path d="M12 18v3" />
      </svg>
    </button>
  );
}

const fmt = (ms) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** What replaces the field while a note records: the live words, a level, a clock, and what the finger may do. */
export function RecordingStrip({ recorder, language = 'en' }) {
  const t = (k) => voiceText(language, k);
  const { phase, ms, bars, words, error } = recorder;
  if (phase === 'idle') return error ? <div className={`${styles.root} ${styles.error}`} role="alert" data-testid="voice-error">{error}</div> : null;
  const sending = phase === 'sending';
  return (
    <div className={`${styles.root} ${styles.strip}`} data-testid="recording-strip" dir={isRtlVoice(language) ? 'rtl' : 'ltr'}>
      <div className={styles.live} dir="auto" aria-live="polite" data-testid="live-words">{words || (sending ? t('hearingNow') : <span className={styles.liveHint}>{phase === 'recording' ? t('slideUp') : ''}</span>)}</div>
      <div className={styles.row}>
        {/* The hint sits on the side the finger slides toward to cancel: the start edge (right in Hebrew and Arabic). */}
        {phase === 'locked' ? null : <span className={styles.hint}>{sending ? t('sendingNote') : t('slideCancel')}</span>}
        <span className={styles.dot} aria-hidden="true" />
        <span className={styles.timer} role="timer" aria-label={t('recording')}>{fmt(ms)}</span>
        <span className={styles.wave} aria-hidden="true">
          {Array.from({ length: 28 }, (_, i) => {
            const v = bars[bars.length - 28 + i] ?? 0;
            return <i key={i} className={styles.bar} style={{ transform: `scaleY(${Math.max(0.12, Math.min(1, v * 6))})` }} />;
          })}
        </span>
        {phase === 'locked' ? (
          <>
            <button type="button" className={`${styles.action} ${styles.actionDanger}`} onClick={recorder.cancel} aria-label={t('discard')} data-testid="voice-discard">{t('discard')}</button>
            <button type="button" className={`${styles.action} ${styles.actionPrimary}`} onClick={recorder.send} aria-label={t('send')} data-testid="voice-send">{t('send')}</button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Voice mode's one screen: a face, one status line, the live words, and Mute / Stop / Type.
 * `figure({ state, level })` is the host's own character (the store's Luna, the portal's StylonCharacter); it sits inside
 * a soft ring that follows the mic level (brighter while hearing, a slow steady pulse while she speaks). Without a
 * figure the plain orb is drawn. Under reduced motion the ring is gone: a still figure and the status line.
 */
export function VoiceModeView({ voice, language = 'en', onType, figure }) {
  const t = (k) => voiceText(language, k);
  const { state, label, words, caption, level, error } = voice;
  const [typed, setTyped] = useState('');
  const editing = state === 'editing';
  // The figure is the big tap target: send now while she hears you, interrupt while she speaks, try again when it failed.
  const figureAction = state === 'hearing' ? 'sendNow' : state === 'speaking' ? 'interrupt' : state === 'failed' ? 'couldntHear' : null;
  const onFigure = voice.tapFigure || (state === 'failed' ? voice.retry : state === 'speaking' ? voice.interrupt : undefined);
  const tappable = state === 'failed' || state === 'speaking';
  const onStatus = state === 'failed' ? voice.retry : voice.interrupt;
  const Status = tappable ? 'button' : 'div';
  const live = state === 'listening' || state === 'hearing';
  // The heard words belong to the person until the turn is sent: while hearing or listening a tap fixes them by typing.
  const fixable = (state === 'hearing' || state === 'listening') && !!words && !!voice.beginEdit;
  useEffect(() => { if (editing) setTyped(words || ''); }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const ringScale = 1 + Math.min(0.35, (live ? level : 0) * 3);
  const ringOpacity = state === 'hearing' ? 0.75 : state === 'listening' ? 0.35 : state === 'speaking' ? 0.5 : state === 'thinking' ? 0.2 : 0.1;
  return (
    <div className={`${styles.root} ${styles.mode} ${styles[`state_${state}`] || ''}`} dir={isRtlVoice(language) ? 'rtl' : 'ltr'} data-testid="voice-mode" data-state={state}>
      <button type="button" className={styles.face} data-testid="voice-figure" onClick={onFigure} aria-label={figureAction ? (figureAction === 'speaking' ? `${label}. ${t('interrupt')}` : t(figureAction === 'interrupt' ? 'interrupt' : figureAction)) : label} aria-disabled={figureAction ? undefined : 'true'} tabIndex={figureAction ? 0 : -1}>
        <span className={styles.ring} data-testid="voice-ring" style={{ transform: `scale(${ringScale.toFixed(3)})`, opacity: ringOpacity }} aria-hidden="true" />
        {figure ? figure({ state, level }) : (
          <div className={styles.orb} style={{ transform: `scale(${1 + Math.min(0.5, level * 4)})` }} aria-hidden="true"><div className={styles.orbCore} /></div>
        )}
        {state === 'muted' ? (
          <span className={styles.badge} data-testid="voice-muted-badge" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" focusable="false"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 12 5" /><path d="M3 3l18 18" /></svg>
          </span>
        ) : null}
      </button>
      <Status {...(tappable ? { type: 'button', onClick: onStatus, 'aria-label': state === 'speaking' ? `${label}. ${t('interrupt')}` : label } : {})} className={styles.status} role="status" aria-live="polite" data-testid="voice-status">
        <span className={styles.statusDot} aria-hidden="true" />
        {label}
      </Status>
      {editing ? (
        <div className={styles.editRow}>
          <input className={styles.editField} dir="auto" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') voice.submitEdit(typed); }} aria-label={t('editHeard')} data-testid="voice-edit" autoFocus />
          <button type="button" className={`${styles.control} ${styles.controlPrimary}`} onClick={() => voice.submitEdit(typed)} data-testid="voice-edit-done">{t('send')}</button>
          <button type="button" className={styles.control} onClick={voice.cancelEdit} data-testid="voice-edit-cancel">{t('cancel')}</button>
        </div>
      ) : fixable ? (
        <button type="button" className={`${styles.words} ${styles.wordsButton}`} dir="auto" onClick={voice.beginEdit} aria-label={`${words}. ${t('editHeard')}`} data-testid="voice-words">{words}</button>
      ) : (
        <div className={styles.words} dir="auto" aria-live="polite" data-testid="voice-words">{state === 'hearing' || state === 'listening' || state === 'failed' ? words : ''}</div>
      )}
      {state === 'speaking' && caption ? <div className={styles.caption} dir="auto" aria-live="off" data-testid="voice-caption">{caption}</div> : null}
      {error ? <div className={styles.error} role="alert" data-testid="voice-error">{error}</div> : null}
      {editing ? null : (
        <div className={styles.controls}>
          <button type="button" className={styles.control} onClick={state === 'muted' ? voice.unmute : voice.mute} aria-pressed={state === 'muted' ? 'true' : 'false'} data-testid="voice-mute">{state === 'muted' ? t('unmute') : t('mute')}</button>
          {/* While she speaks it stops her talking and listens on; otherwise it ends voice mode (owner, 2 Nov 2026: Stop closed
              the conversation when they meant to stop her reading). */}
          <button type="button" className={`${styles.control} ${styles.controlStop}`} onClick={state === 'speaking' ? voice.interrupt : voice.stop} aria-label={state === 'speaking' ? t('stopTalking') : t('closeVoice')} data-testid="voice-stop">{state === 'speaking' ? t('stopTalking') : t('end')}</button>
          <button type="button" className={styles.control} onClick={() => { voice.stop(); onType?.(); }} aria-label={t('typeInstead')} data-testid="voice-type">{t('type')}</button>
        </div>
      )}
    </div>
  );
}
